"use client";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { getMessaging, isSupported, onMessage } from "firebase/messaging";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { useNotifications } from "~/components/notifications/NotificationsContext";
import { useSnackbar } from "~/components/snackbar";
import { customerNotificationsService } from "~/connectors/notifications.connector";
import { getFirebaseApp } from "~/lib/firebase";
import notificationsService from "~/services/notifications.service";


/**
 * Handles Firebase push notification events in the foreground and syncs the
 * FCM device token on every app load to keep it fresh.
 *
 * - Listens for foreground messages via onMessage and shows a snackbar.
 * - On each authenticated load, silently calls getToken() and upserts it in
 *   the backend so the "last updated" timestamp stays current and token
 *   changes are detected automatically (token refresh scenario).
 *
 * Renders nothing — mount this inside <Providers> after authentication is set up.
 */
export default function PushNotificationHandler() {
  const { status } = useSession();
  const isAuthenticated = status === "authenticated";
  const { refresh } = useNotifications();
  const { showInfo } = useSnackbar();
  const router = useRouter();

  // Foreground message listener
  useEffect(() => {
    if (!isAuthenticated) return;
    if (typeof window === "undefined") return;

    let unsubscribe: (() => void) | undefined;

    const setupListener = async () => {
      const app = getFirebaseApp();
      if (!app) return;

      try {
        const supported = await isSupported();
        if (!supported) return;

        const messaging = getMessaging(app);
        unsubscribe = onMessage(messaging, (payload) => {
          const messageText =
            payload.notification?.title ??
            payload.data?.title ??
            "Notificare nouă";
          const actionUrl =
            payload.data?.url || payload.data?.actionUrl || undefined;
          const id =
            payload.data?.notificationId || payload.data?.id || undefined;

          const onClick = async () => {
            try {
              if (id) await customerNotificationsService.markAsRead(id);
            } catch {
              // ignore
            }

            if (actionUrl) {
              try {
                const parsed = new URL(actionUrl, window.location.origin);
                if (parsed.origin === window.location.origin) {
                  const relative =
                    parsed.pathname + parsed.search + parsed.hash;
                  router.push(relative);
                  return;
                }
              } catch {
                // parsing failed
              }
              window.open(actionUrl, "_blank");
            }
          };

          showInfo({
            message: messageText,
            buttonLabel: actionUrl ? "Vezi" : undefined,
            onClick,
            icon: <NotificationsIcon fontSize="inherit" />,
          });
          refresh();
        });
      } catch {
        // Firebase not available or not configured — silently skip
      }
    };

    setupListener();
    return () => {
      unsubscribe?.();
    };
  }, [isAuthenticated, refresh, showInfo, router]);

  // Token sync on every authenticated load
  useEffect(() => {
    if (!isAuthenticated) return;
    if (typeof window === "undefined") return;
    if (!notificationsService.isPushSupported()) return;
    // Respect local browser preference: if the user disabled push in this browser,
    // do not auto-register or sync tokens on load.
    try {
      if (!notificationsService.isPushEnabledPref()) return;
    } catch (err) {
      // ignore import/localStorage errors and proceed
    }

    if (
      typeof Notification === "undefined" ||
      Notification.permission !== "granted"
    )
      return;

    const syncToken = async () => {
      try {
        // Read the previously known token before fetching a fresh one. If FCM
        // returns the same token, there's no need to hit the backend again.
        const previousToken = notificationsService.getStoredToken();

        const token = await notificationsService.getTokenSilently();
        if (!token) return;

        if (previousToken === token) return;

        const { deviceName, deviceType } = notificationsService.getDeviceInfo();
        await customerNotificationsService.registerDevice(
          token,
          deviceName,
          deviceType,
        );
      } catch {
        // Silently ignore token sync failures
      }
    };

    syncToken();
  }, [isAuthenticated]);

  return null;
}
